import { useCallback, useEffect, useRef, useState } from 'react'
import type { Quapp } from '../../../types/quapp'

/**
 * The push capability channel between an installed Quapp and the store shell.
 *
 * Why this exists: the installed PWA (and therefore the only service-worker scope that
 * can receive web push when the app is closed) belongs to store.quapp.io — the Quapp
 * runs in a cross-origin iframe and cannot subscribe itself. So the Quapp asks over
 * postMessage and the shell does the subscribing:
 *
 *   Quapp → shell : { type: 'quapp:push:subscribe', vapidPublicKey }
 *   shell → Quapp : { type: 'quapp:push:subscription', subscription | null }
 *
 * Permission UX: if permission was already granted we subscribe silently. Otherwise we
 * show a small shell-owned prompt — the Enable tap is a same-origin user gesture, which
 * is exactly what iOS requires for Notification.requestPermission() in an installed PWA.
 * Messages are only accepted from (and replied to) the Quapp's own origin.
 */

type AskState = { vapidKey: string; source: Window } | null

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(b64)
  const out = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i)
  return out
}

export function PushBridge({ quapp }: { quapp: Quapp }) {
  const [ask, setAsk] = useState<AskState>(null)
  const [busy, setBusy] = useState(false)
  const originRef = useRef<string | null>(null)

  useEffect(() => {
    try {
      originRef.current = new URL(quapp.quapp_url).origin
    } catch {
      originRef.current = null
    }
  }, [quapp.quapp_url])

  const reply = useCallback((target: Window, subscription: unknown) => {
    const origin = originRef.current
    if (!origin) return
    try {
      target.postMessage({ type: 'quapp:push:subscription', subscription }, origin)
    } catch {
      /* target gone — nothing to do */
    }
  }, [])

  const subscribe = useCallback(
    async (vapidKey: string, target: Window) => {
      setBusy(true)
      try {
        if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
          const perm = await Notification.requestPermission()
          if (perm !== 'granted') {
            reply(target, null)
            return
          }
        }
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') {
          reply(target, null)
          return
        }
        const reg = await navigator.serviceWorker.ready
        const existing = await reg.pushManager.getSubscription()
        const sub =
          existing ??
          (await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidKey) as unknown as ArrayBuffer,
          }))
        reply(target, sub.toJSON())
      } catch (err) {
        console.warn('[PushBridge] subscribe failed', err)
        reply(target, null)
      } finally {
        setBusy(false)
        setAsk(null)
      }
    },
    [reply]
  )

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const origin = originRef.current
      if (!origin || event.origin !== origin) return
      const data = event.data as { type?: string; vapidPublicKey?: string } | null
      if (data?.type !== 'quapp:push:subscribe' || typeof data.vapidPublicKey !== 'string') return
      const source = event.source as Window | null
      if (!source) return

      if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
        reply(source, null)
        return
      }
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        void subscribe(data.vapidPublicKey, source) // silent — already allowed
        return
      }
      if (typeof Notification !== 'undefined' && Notification.permission === 'denied') {
        reply(source, null)
        return
      }
      setAsk({ vapidKey: data.vapidPublicKey, source }) // needs the shell-gesture prompt
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [reply, subscribe])

  if (!ask) return null

  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-4 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
      <div className="mx-auto max-w-sm rounded-2xl border border-slate-200 bg-white/95 p-5 shadow-2xl backdrop-blur">
        <div className="flex items-center gap-3">
          {quapp.icon_url && (
            <img src={quapp.icon_url} alt="" className="h-10 w-10 rounded-xl object-cover" />
          )}
          <div className="min-w-0">
            <p className="text-sm font-semibold text-slate-900">{quapp.display_name} would like to send reminders</p>
            <p className="mt-0.5 text-xs text-slate-500">Routine alarms arrive even when the app is closed.</p>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-2">
          <button
            onClick={() => void subscribe(ask.vapidKey, ask.source)}
            disabled={busy}
            className="flex-1 rounded-full bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          >
            {busy ? 'Enabling…' : 'Enable reminders'}
          </button>
          <button
            onClick={() => {
              reply(ask.source, null)
              setAsk(null)
            }}
            className="rounded-full px-4 py-2.5 text-sm font-medium text-slate-500"
          >
            Not now
          </button>
        </div>
      </div>
    </div>
  )
}
